// @ts-check
// Rotas REST da agenda e das contas conectadas (docs/arquitetura-appcrm/08-contrato-agenda.md, seções 5 e 6).
import { api, requestFile } from './api-client.js';

/**
 * @typedef {{ handle: string | null, displayName: string | null, isOrganizer: boolean, responseStatus: 'NEEDS_ACTION' | 'DECLINED' | 'TENTATIVE' | 'ACCEPTED',
 *   personId: string | null, workspaceMemberId: string | null, person: { id: string, name: string } | null, workspaceMemberName?: string | null }} CalendarParticipant
 * @typedef {{ id: string, kind: 'EVENT' | 'OPPORTUNITY_DATE' | 'SCHOOL_EVENT', eventType: string, source: string, title: string | null,
 *   description: string | null, location: string | null, conferenceLink?: string | null, startsAt: string, endsAt: string, isFullDay: boolean,
 *   isCanceled: boolean, visibility: string, isRestricted: boolean, iCalUid?: string, sequence?: number, invitationSentAt?: string | null,
 *   fieldName?: string, enrollmentOpportunity: { id: string, name: string, stage: string | null, ownerId?: string | null } | null,
 *   organizer: { workspaceMemberId: string, name: string, email: string | null } | null, participants: CalendarParticipant[],
 *   connectedAccounts: Array<{ id: string, provider: string, handle: string }>, href: string | null }} CalendarItem
 * @typedef {{ status: 'SENT' | 'SKIPPED' | 'FAILED', recipients: string[], reason?: string }} InvitationResult
 * @typedef {{ startsAt?: string, endsAt?: string, workspaceMemberId?: string | null, eventTypes?: string[], enrollmentOpportunityId?: string,
 *   personId?: string, householdId?: string, includeCanceled?: boolean }} CalendarQuery
 * @typedef {{ id: string, provider: 'GOOGLE' | 'MICROSOFT', handle: string, displayName: string | null, ownerWorkspaceMemberId: string | null,
 *   ownerName: string | null, isMine: boolean, messageChannelId: string | null, messageVisibility: 'METADATA' | 'SUBJECT' | 'SHARE_EVERYTHING',
 *   calendarVisibility: 'METADATA' | 'SHARE_EVERYTHING', isMessageSyncEnabled: boolean, isCalendarSyncEnabled: boolean, messageSyncStatus: string,
 *   calendarSyncStatus: string, messageSyncedAt: string | null, calendarSyncedAt: string | null, authFailedAt: string | null, lastError: string | null,
 *   contactAutoCreationPolicy?: 'NONE' | 'SENT' | 'SENT_AND_RECEIVED', excludeNonProfessionalEmails?: boolean, excludeGroupEmails?: boolean,
 *   canCreateEvents?: boolean }} ConnectedAccount
 *   canCreateEvents: a conta Google concedeu criar eventos (páginas de agendamento, 3.0.0)
 */

/**
 * @param {CalendarQuery} query
 * @returns {Promise<CalendarItem[]>}
 */
export async function listCalendarEvents(query) {
  const body = await api.get('/rest/calendar-events', {
    startsAt: query.startsAt,
    endsAt: query.endsAt,
    workspaceMemberId: query.workspaceMemberId ?? undefined,
    eventTypes: query.eventTypes?.length ? query.eventTypes.join(',') : undefined,
    enrollmentOpportunityId: query.enrollmentOpportunityId,
    personId: query.personId,
    householdId: query.householdId,
    includeCanceled: query.includeCanceled ? 'true' : undefined,
  });
  return body?.data?.events ?? [];
}

/**
 * Agenda pelo card: grava a data do card, cria ou remarca o evento e manda o convite.
 * @param {{ enrollmentOpportunityId: string, eventType: string, startsAt: string, durationMinutes?: number, organizerWorkspaceMemberId?: string | null,
 *   location?: string | null, description?: string | null, sendInvitation?: boolean }} input
 * @returns {Promise<{ event: CalendarItem, isRescheduled: boolean, invitation: InvitationResult }>}
 */
export async function scheduleOpportunityEvent(input) {
  const body = await api.post('/rest/calendar-events/schedule', input);
  return body.data;
}

/**
 * @param {Record<string, any>} input
 * @returns {Promise<{ event: CalendarItem, invitation: InvitationResult }>}
 */
export async function createCalendarEvent(input) {
  const body = await api.post('/rest/calendar-events', input);
  return body.data;
}

/**
 * @param {string} eventId @param {Record<string, any>} patch
 * @returns {Promise<{ event: CalendarItem, invitation: InvitationResult }>}
 */
export async function updateCalendarEvent(eventId, patch) {
  const body = await api.patch(`/rest/calendar-events/${eventId}`, patch);
  return body.data;
}

/**
 * @param {string} eventId @param {{ sendInvitation?: boolean }} [options]
 * @returns {Promise<{ event: CalendarItem, invitation: InvitationResult }>}
 */
export async function cancelCalendarEvent(eventId, options = {}) {
  const body = await api.post(`/rest/calendar-events/${eventId}/cancel`, options);
  return body.data;
}

/** @param {string} eventId */
export function downloadEventInvitation(eventId) {
  return requestFile(`/rest/calendar-events/${eventId}/invitation.ics`);
}

/**
 * @returns {Promise<{ accounts: ConnectedAccount[], providers: Record<'GOOGLE' | 'MICROSOFT', { isConfigured: boolean }> }>}
 */
export async function listConnectedAccounts() {
  const body = await api.get('/rest/connected-accounts');
  return body.data;
}

/**
 * @param {'google' | 'microsoft'} provider
 * @param {{ calendarEvents?: boolean, connectedAccountId?: string, returnTo?: 'ACCOUNTS' | 'BOOKING_PAGES' | 'BOOKING_PAGE', returnToId?: string, bookingHost?: boolean }} [options]
 *   `calendarEvents`: pede ao Google também criar eventos (anfitrião); `connectedAccountId`: a conta do próprio membro
 *   que ganha o escopo (o Google já abre nela); `returnTo`: a tela para onde o retorno do Google leva (`BOOKING_PAGE`
 *   com `returnToId` volta ao editor daquela página); `bookingHost`: grava junto o aceite de ser anfitrião (3.5.0)
 * @returns {Promise<string>}
 */
export async function startAccountConnection(provider, options = {}) {
  const body = await api.post(`/rest/connected-accounts/oauth/${provider}/start`, options.calendarEvents ? {
    calendarEvents: true,
    ...(options.connectedAccountId ? { connectedAccountId: options.connectedAccountId } : {}),
    ...(options.returnTo ? { returnTo: options.returnTo } : {}),
    ...(options.returnToId ? { returnToId: options.returnToId } : {}),
    ...(options.bookingHost ? { bookingHost: true } : {}),
  } : {});
  return body.data.authorizationUrl;
}

/** @param {string} accountId @param {Partial<ConnectedAccount>} patch @returns {Promise<ConnectedAccount>} */
export async function updateConnectedAccount(accountId, patch) {
  const body = await api.patch(`/rest/connected-accounts/${accountId}`, patch);
  return body.data.account;
}

/** @param {string} accountId @returns {Promise<{ account: ConnectedAccount, result: any }>} */
export async function syncConnectedAccountNow(accountId) {
  const body = await api.post(`/rest/connected-accounts/${accountId}/sync`, {});
  return body.data;
}

/** @param {string} accountId */
export async function disconnectAccount(accountId) {
  await api.delete(`/rest/connected-accounts/${accountId}`);
}
